import { useCallback, useEffect, useMemo, useState } from "react";

export interface DocumentFolder {
  id: number;
  name: string;
  description?: string | null;
  ownerType: "agency" | "client" | "prospect";
  agencyId?: string | null;
  clientId?: number | null;
  prospectId?: number | null;
  visibleToEmployees?: boolean;
  createdAt?: string;
  updatedAt?: string;
  docCount?: number;
}

interface UseDocumentFoldersOptions {
  ownerType: "agency" | "client" | "prospect";
  clientId?: number;
  prospectId?: number;
  autoRefresh?: boolean;
  parentId?: number | null;
}

export function useDocumentFolders(options: UseDocumentFoldersOptions) {
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("ownerType", options.ownerType);
    if (options.ownerType === "client" && options.clientId != null) {
      params.set("clientId", String(options.clientId));
    }
    if (options.ownerType === "prospect" && options.prospectId != null) {
      params.set("prospectId", String(options.prospectId));
    }
    if (options.parentId === null) params.set("parentId", "null");
    else if (typeof options.parentId === "number")
      params.set("parentId", String(options.parentId));
    return params.toString();
  }, [options.ownerType, options.clientId, options.prospectId, options.parentId]);

  const fetchFolders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/document-folders?${queryString}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load folders");
      const data = await res.json();
      setFolders(Array.isArray(data.folders) ? data.folders : []);
    } catch (e: any) {
      setError(e.message || "Failed to load folders");
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const createFolder = async (
    name: string,
    description?: string | null,
    visibleToEmployees?: boolean
  ) => {
    const body: any = {
      name,
      description: description || null,
      ownerType: options.ownerType,
  parentId: options.parentId ?? null,
      visibleToEmployees: visibleToEmployees ?? false,
    };
    if (options.ownerType === "client") body.clientId = options.clientId;
    if (options.ownerType === "prospect") body.prospectId = options.prospectId;
    const res = await fetch(`/api/document-folders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to create folder");
    }
    const folder = await res.json();
    if (options.autoRefresh !== false) await fetchFolders();
    return folder as DocumentFolder;
  };

  const updateFolder = async (
    id: number,
    updates: Partial<
      Pick<DocumentFolder, "name" | "description" | "visibleToEmployees">
    >
  ) => {
    const res = await fetch(`/api/document-folders/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to update folder");
    }
    const folder = await res.json();
    if (options.autoRefresh !== false) await fetchFolders();
    return folder as DocumentFolder;
  };

  const deleteFolder = async (id: number) => {
    const res = await fetch(`/api/document-folders/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to delete folder");
    }
    if (options.autoRefresh !== false) await fetchFolders();
    return true;
  };

  return {
    folders,
    loading,
    error,
    refreshFolders: fetchFolders,
    createFolder,
    updateFolder,
    deleteFolder,
  };
}


